import {Play} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  NativeSelect,
  NativeSelectOption
} from '@kroll-resolver-crisp/ui-components-react';
import {type QueueFilters} from '@/lib/queueFilters';

type Props = {
  readonly matching: number;
  readonly filters: QueueFilters;
  // The queue group being worked, by name, and its members as labels; it starts without a vertical.
  readonly group: {readonly name: string; readonly sources: readonly string[]} | null;
  // How many items one claim takes, and the batch sizes an admin allows besides one.
  readonly size: number;
  readonly batchSizes: readonly number[];
  // A channel is always reviewed one at a time, so the choice is fixed.
  readonly sizeLocked: boolean;
  readonly onSizeChange: (size: number) => void;
  // The analyst has no vertical to review at all, as opposed to filters that match nothing.
  readonly noAccess: boolean;
  readonly disabled: boolean;
  readonly onStart: () => void;
};

// Where an analyst starts: how much work matches the filters, and the button that claims it. A claim
// is one vertical or a queue group, so with "All verticals" the counts show but Start waits for one.
export function QueueStartCard({
  matching,
  filters,
  group,
  size,
  batchSizes,
  sizeLocked,
  noAccess,
  disabled,
  onSizeChange,
  onStart
}: Props) {
  if (matching === 0) {
    return (
      <Empty className="m-auto">
        <EmptyHeader>
          <EmptyTitle>Nothing to review</EmptyTitle>
          <EmptyDescription>
            {noAccess
              ? 'Your account does not have access assigned to any vertical moderation queues.'
              : 'No unclaimed items match these filters. Widen the filters or check back later.'}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="m-auto w-full max-w-md p-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {matching} item{matching === 1 ? '' : 's'} ready
          </CardTitle>
          <CardDescription>
            {group?.name ?? filters.vertical ?? 'All verticals'} · newest first
            {size > 1 ? ` · ${size} at a time` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {group ? (
            <div className="text-xs">
              <span className="text-muted-foreground font-medium">From</span>
              <ul className="mt-1 space-y-0.5">
                {group.sources.map((source) => (
                  <li key={source} className="truncate" title={source}>
                    {source}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="flex items-end gap-3">
            <label className="text-muted-foreground flex flex-col gap-1 text-xs font-medium">
              Items
              <NativeSelect
                value={size}
                disabled={sizeLocked}
                onChange={(event) => {
                  onSizeChange(Number(event.target.value));
                }}>
                <NativeSelectOption value={1}>1 item</NativeSelectOption>
                {batchSizes.map((option) => (
                  <NativeSelectOption key={option} value={option}>
                    {option} items
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </label>
            <Button
              type="button"
              className="flex-1"
              disabled={disabled || (!filters.vertical && !group)}
              onClick={onStart}>
              <Play />
              Start reviewing
            </Button>
          </div>
          {sizeLocked ? (
            <p className="text-muted-foreground text-xs">Channels are reviewed one at a time.</p>
          ) : null}
          {group ? (
            <p className="text-muted-foreground text-xs">
              Each batch comes from one vertical: the one with the newest matching item.
            </p>
          ) : filters.vertical ? null : (
            <p className="text-muted-foreground text-xs">
              Choose a vertical or a group to start: a claim covers one vertical at a time.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
